// 按键值参考 https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
/**
 * 键盘事件处理类
 * @module Keyboard
 * @description
 * 按键值参考 https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
 * @example
 * const keyboard = new Keyboard();
 * // 基础用法
 * keyboard.on('Enter', e => {
 *   // do something
 * });
 *
 * // 组合键
 * keyboard.on({
 *   key: 'Enter',
 *   alt: true,
 *   ctrl: true,
 *   shift: true
 * }, e => {
 *   // do something
 * })
 *
 * // 功能键,  浏览器占用了 F1（新tab）、F3（搜索）、F5（刷新）、F6（定位地址栏）、F7（开启光标模式）、F11（全屏）、F12（开发者工具）
 * keyboard.on('F8', e => {
 *   // do something
 * })
 */
import { on, off } from './dom';
import { uid } from './util';

// 组合键修饰符
const modifiers = ['alt', 'ctrl', 'meta', 'shift'];
// 按键别名
const keyNames = {
  esc: 'Escape',
  space: ' ',
  up: 'ArrowUp',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  down: 'ArrowDown',
  delete: 'Backspace'
};

function createKeyObject(key) {
  return typeof key === 'object' ? key : { key };
}

function withModifiers(keyObject, e) {
  return modifiers.every(name => {
    const key = keyObject[name];
    return !key || key === e[`${name}Key`];
  });
}

function withKeys(keyObject, e) {
  if (!keyObject.key) return false;
  const key = keyNames[keyObject.key] || keyObject.key;
  return key === e.key;
}

function createHandler(instance, keyObject, func) {
  return e => {
    if (withKeys(keyObject, e) && withModifiers(keyObject, e)) {
      const { disabled, config } = instance;
      if (disabled) return;
      const { stop, prevent } = config;
      stop && e.stopPropagation();
      prevent && e.preventDefault();
      func(e);
    }
  };
}

/**
 * Keyboard
 * @class
 */
class Keyboard {
  /**
   * 构造函数
   * @param {object} config
   * @param {string} [config.type=keyup] 事件类型，可选 keyup / keypress / keydown
   * @param {HTMLElement} [config.el=document.body] 事件触发元素
   * @param {boolean} [config.stop] 阻止事件冒泡
   * @param {boolean} [config.prevent] 不要执行与事件关联的默认动作
   */
  constructor(config = {}) {
    this.config = Object.assign({
      // 事件类型
      type: 'keyup',
      // 事件触发源
      el: document.body,
      stop: false,
      prevent: false
    }, config);
    this.handlers = {};
    // 是否禁用
    this.disabled = false;
  }

  /**
   * 绑定事件
   * @param {string|object} key 按键或按键组合对象
   * @param {string} key.key 按键
   * @param {boolean} [key.alt] 组合键altKey
   * @param {boolean} [key.ctrl] 组合键ctrlKey
   * @param {boolean} [key.shift] 组合键shiftKey
   * @param {boolean} [key.meta] 组合键 metaKey
   * @param {function} callback 事件处理函数
   */
  on(key, callback) {
    const keyObject = createKeyObject(key);
    const id = uid();
    const handler = createHandler(this, keyObject, callback);
    const { type, el } = this.config;
    on(el, type, handler);
    this.handlers[id] = {
      key: keyObject,
      handler
    };
    return id;
  }

  /**
   * 销毁事件
   * @param {string} id 事件标识， on 的返回值
   */
  off(id) {
    const { type, el } = this.config;
    if (!id) {
      Object.values(this.handlers).forEach(item => off(el, type, item.handler));
      this.handlers = {};
      return;
    }
    const item = this.handlers[id];
    if (item) {
      off(el, type, item.handler);
      delete this.handlers[id];
    }
  }

  /**
   * 绑定事件，只触发一次
   * @param {string|object} key 按键或按键组合对象
   * @param {string} key.key 按键
   * @param {boolean} [key.alt] 组合键altKey
   * @param {boolean} [key.ctrl] 组合键ctrlKey
   * @param {boolean} [key.shift] 组合键shiftKey
   * @param {boolean} [key.meta] 组合键 metaKey
   * @param {function} callback 事件处理函数
   */
  once(key, callback) {
    const id = this.on(key, e => {
      callback && callback(e);
      this.off(id);
    });
  }

  /**
   * 手动触发事件
   * @param {string} id 触发事件id，on的返回值
   */
  fire(id) {
    const item = this.handlers[id];
    if (!item) return;
    const keyObject = item.key;
    const dict = {
      key: keyObject.key
    };
    modifiers.forEach(name => {
      const keyName = `${name}Key`;
      dict[keyName] = !!keyObject[name];
    });
    const e = new KeyboardEvent(this.config.type, dict);
    item.handler(e);
  }

  /**
   * 销毁
   */
  destroy() {
    this.off();
  }
}

export default Keyboard;
